import { useModal } from '@ebay/nice-modal-react'
import { BsSearch } from 'react-icons/bs'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useInfiniteQuery, useMutation } from 'react-query'

import clsx from 'clsx'
import NoData from 'components/NoData'
import Button from 'components/form/button'
import Input from 'components/form/input'
import { privateRequest } from 'config/axios.config'
import queryClient from 'config/queyClient.config'
import addUpdatebankModal from 'features/banks/addUpdatebank.modal'
import { useState } from 'react'
import { LoaderIcon } from 'react-hot-toast'
import { dateFormatter } from 'utils'
import { errorHandler } from 'utils/errorHandler'
import useDebounce from 'utils/useDebounce'

interface DeletePayload {
  id: string
}
interface UpdatePayload {
  id: string
  status: string
}
export default function BankPage() {
  const addUpdateBanks = useModal(addUpdatebankModal)

  const [search, setSearch] = useState<string>('')
  const debouncedSearchTerm = useDebounce(search, 500)

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery<BankResponse, Error>(
    ['bank', debouncedSearchTerm],
    async ({ pageParam = 1 }) => {
      try {
        const res = await privateRequest.get(
          `admin/bank?page=${pageParam}&query=${debouncedSearchTerm}`,
        )
        return res.data.data
      } catch (error) {
        errorHandler(error)
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  )
  const deleteMedication = useMutation<{ message: string }, Error, DeletePayload>(
    async (payload) => {
      try {
        const res = await privateRequest.delete(`admin/bank/${payload.id}`)
        return res.data
      } catch (error) {
        errorHandler(error)
        throw error
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank')
      },
    },
  )
  const updatebank = useMutation<{ message: string }, Error, UpdatePayload>(
    async (payload) => {
      try {
        const res = await privateRequest.patch('admin/bank', payload)
        return res.data
      } catch (error) {
        errorHandler(error)
        throw error
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bank')
      },
    },
  )
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const toggleStatus = async (row: Bank) => {
    try {
      setUpdatingId(row._id)
      await updatebank.mutateAsync({
        ...row,
        status: row.status === 'active' ? 'inactive' : 'active',
        id: row._id,
      })
    } finally {
      setUpdatingId(null)
    }
  }

  const banks = data?.pages?.flatMap((page) => page.docs) ?? []

  return (
    <div className='card'>
      <div className='flex flex-wrap gap-4 mb-10'>
        <Button
          icon='add'
          onClick={() => addUpdateBanks.show({ title: 'Add New Bank' })}
          variant='outlined'
          size='md'
        >
          Add New
        </Button>
        <Input
          variant='outlined'
          placeholder='Name/Phone'
          size='md'
          className='md:!w-[230px] ml-auto'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          afterFix={<BsSearch className='mr-3 text-gray' size={20} />}
        />
      </div>
      <div
        id='scrollableDiv'
        className='h-[calc(100vh-340px)] md:h-[calc(100vh-325px)] overflow-y-auto'
      >
        <InfiniteScroll
          dataLength={banks.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={
            <div className='flex gap-2 justify-center items-center'>
              <LoaderIcon />
              Loading...
            </div>
          }
          scrollableTarget='scrollableDiv'
        >
          <table
            className={clsx({
              'blur-sm animate-pulse': isLoading,
            })}
          >
            <thead>
              <tr>
                <td>Created date</td>
                {/* <td>PID</td> */}
                <td>Name</td>
                <td>Type</td>
                <td>Status</td>
                <td>Action</td>
              </tr>
            </thead>

            <tbody>
              {isLoading && (
                <>
                  {Array(10)
                    .fill(0)
                    .map((_, i) => (
                      <tr key={i}>
                        <td>Day Month, Year</td>
                        <td>NOCODE</td>
                        <td>
                          <p className='text-primary'>John Doe</p>
                        </td>
                        <td>0123456789</td>
                      </tr>
                    ))}
                </>
              )}
              {banks.map((row) => (
                <tr key={row._id}>
                  <td className='whitespace-nowrap'>{dateFormatter(row.createdAt)}</td>
                  {/* <td>{row._id}</td>/ */}
                  <td>{row.title}</td>
                  <td className='capitalize'>{row.type}</td>
                  <td
                    className={clsx(
                      'capitalize cursor-pointer',
                      row.status === 'active' ? 'text-green-600' : 'text-red-500',
                    )}
                    onClick={() => toggleStatus(row)}
                  >
                    {updatingId === row._id ? 'Updating...' : row.status}
                  </td>

                  <td>
                    <div className='inline-flex gap-2'>
                      <Button
                        onClick={() => addUpdateBanks.show({ bank: row, title: 'Update Bank' })}
                        size='sm'
                        color='default'
                      >
                        Edit
                      </Button>
                      <Button
                        size='sm'
                        color='danger'
                        onClick={() => deleteMedication.mutate({ id: row._id })}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
        {!banks?.length && !isLoading && <NoData />}
      </div>
    </div>
  )
}
